import { useSearchParams, Link } from 'react-router-dom';
import { Wallet, Stamp, Globe2, type LucideIcon } from 'lucide-react';
import SEO from '@/components/common/SEO';
import { GuideCard, ObangsaekStripe } from '@/components/editorial';
import { GUIDES, guidesByCategory } from '@/content/guides/registry';
import { GUIDE_CATEGORIES, categoryVars, type GuideCategoryId } from '@/content/guides/types';

const ICONS: Record<string, LucideIcon> = { wallet: Wallet, stamp: Stamp, globe: Globe2 };

const BASE_URL = 'https://koreanhub.net';

export default function Guides() {
  const [params, setParams] = useSearchParams();
  const active = params.get('category') as GuideCategoryId | null;
  const validActive = active && active in GUIDE_CATEGORIES ? active : null;

  const visible = validActive ? guidesByCategory(validActive) : GUIDES;

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'KoreanHub Guides',
    description:
      'Researched, sourced guides on money transfers, visas, and life abroad for the Korean diaspora.',
    url: `${BASE_URL}/guides`,
    hasPart: GUIDES.map((g) => ({
      '@type': 'Article',
      headline: g.title,
      url: `${BASE_URL}/guides/${g.slug}`,
      datePublished: g.publishedAt,
      dateModified: g.updatedAt,
    })),
  };

  return (
    <div className="bg-white dark:bg-surface-dark">
      <SEO
        title="Guides — Money, Visas & Life Abroad for Koreans"
        description="Researched, sourced guides for the Korean diaspora: remittance fees and tax rules, the F-4 visa, and building a professional network abroad."
        path="/guides"
        jsonLd={collectionSchema}
      />

      {/* ── Header ── */}
      <header className="border-b border-ink-200 bg-ink-50 dark:border-surface-dark-border dark:bg-white/[0.02]">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
          <h1 className="text-3xl font-bold leading-tight text-ink-900 dark:text-ink-50 sm:text-4xl">
            Guides
          </h1>
          <p lang="ko" className="mt-1 font-korean text-lg text-ink-500 dark:text-ink-400">
            가이드
          </p>
          <p className="mt-4 max-w-2xl text-base leading-7 text-ink-700 dark:text-ink-200">
            Long-form, sourced answers to the questions that actually cost Korean families money and
            time abroad. Every guide lists its primary sources and the date its figures were last
            verified.
          </p>
        </div>
      </header>

      <ObangsaekStripe />

      {/* ── Category filter ── */}
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <nav className="mb-8 flex flex-wrap gap-2" aria-label="Filter guides by category">
          <button
            type="button"
            onClick={() => setParams({})}
            aria-pressed={!validActive}
            className={
              !validActive
                ? 'rounded-full bg-jeok-500 px-4 py-2 text-sm font-medium text-white dark:bg-jeok-dark'
                : 'rounded-full bg-ink-100 px-4 py-2 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-200 dark:bg-white/10 dark:text-ink-200'
            }
          >
            All guides
          </button>

          {Object.values(GUIDE_CATEGORIES).map((cat) => {
            const Icon = ICONS[cat.icon];
            const isActive = validActive === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setParams({ category: cat.id })}
                aria-pressed={isActive}
                style={categoryVars(cat)}
                className={
                  isActive
                    ? 'inline-flex items-center gap-2 rounded-full bg-[var(--cat-color)] px-4 py-2 text-sm font-medium text-[var(--cat-on)] transition-colors'
                    : 'inline-flex items-center gap-2 rounded-full bg-ink-100 px-4 py-2 text-sm font-medium text-[var(--cat-text)] transition-colors hover:bg-ink-200 dark:bg-white/10 dark:text-[var(--cat-text-dark)]'
                }
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                {cat.label}
              </button>
            );
          })}
        </nav>

        {/* ── Grid ── */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((guide) => (
            <GuideCard key={guide.slug} guide={guide} />
          ))}
        </div>

        {visible.length === 0 && (
          <p className="py-16 text-center text-ink-500 dark:text-ink-400">
            No guides in this category yet.{' '}
            <Link to="/guides" className="font-medium text-cheong-500 underline dark:text-cheong-dark">
              See all guides
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
